import { forwardRef, useRef } from 'react';
import type { KeyboardEvent, MouseEvent, PropsWithoutRef, ReactNode } from 'react';

import { useConsolidatedRef, useTestIds, useUID } from '../../hooks';
import type { HeadingTag, OmitStrict, RefElement } from '../../types';
import { getActiveElement, withTestIds } from '../../utils';
import SelectionCard, { focusableSelector } from '../SelectionCard/SelectionCard';
import type { SelectionCardProps } from '../SelectionCard/SelectionCard.types';
import Text from '../Text';

import RadioButton from './RadioButton';
import type { RadioButtonProps } from './RadioButton';
import { getRadioButtonCardTestIds } from './RadioButton.test-ids';

export type RadioButtonCardProps = OmitStrict<SelectionCardProps, 'heading'> &
  OmitStrict<RadioButtonProps, 'label' | 'variant'> & {
    label: NonNullable<ReactNode>;
    /**
     * Uses specific heading tag for header.
     * @default h4
     */
    headingTag?: HeadingTag;
  };

const RadioButtonCard = forwardRef<
  RefElement<RadioButtonProps>,
  PropsWithoutRef<RadioButtonCardProps>
>(function RadioButtonCard(prop, ref) {
  const uid = useUID();
  const {
    testId,
    id = uid,
    label,
    headingTag = 'h4',
    'aria-label': ariaLabel,
    disabled,
    readOnly,
    onChange,
    onClick,
    onKeyDown,
    status,
    checked,
    value,
    ...restProps
  } = prop;
  const cardId = `${id}-card`;
  const labelId = `${id}-label`;

  const testIds = useTestIds(testId, getRadioButtonCardTestIds);
  const inputRef = useConsolidatedRef(ref);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <SelectionCard
      {...restProps}
      id={cardId}
      testId={testIds}
      aria-labelledby={ariaLabel ? undefined : labelId}
      aria-label={ariaLabel}
      heading={
        <RadioButton
          testId={testIds.root}
          onChange={onChange}
          onClick={onClick}
          status={status}
          checked={checked}
          value={value}
          id={id}
          disabled={disabled}
          readOnly={readOnly}
          aria-labelledby={labelId}
          label={
            <Text variant={headingTag} as='span' id={labelId} data-testid={testIds.label}>
              {label}
            </Text>
          }
          variant='simple'
          ref={inputRef}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key.includes('arrow')) e.preventDefault();
            onKeyDown?.(e);
          }}
        />
      }
      readOnly={readOnly}
      disabled={disabled}
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (readOnly || !containerRef.current?.contains(target)) return;
        if (
          containerRef.current &&
          [...containerRef.current.querySelectorAll(focusableSelector)].includes(target)
        )
          return;
        e.preventDefault();
        inputRef.current?.click();
        (inputRef.current?.hasAttribute('data-main-focus')
          ? inputRef
          : containerRef
        ).current?.focus();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === ' ' && containerRef.current === getActiveElement()) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      ref={containerRef}
    />
  );
});

export default withTestIds(RadioButtonCard, getRadioButtonCardTestIds);
